import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Optional, Output, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TT_VIEW_NAVIGATOR, TTViewNavigator } from '@app/modal/state-modal/state-modal.component';
import { StateService } from '@app/core/services/state.service';
import { GraphProfileService } from '@app/modules/microsoft-graph/services/graph-profile.service';

import { EmailViewerAttachment, EmailViewerMessage, EmailService } from '@app/services/email.service';

/**
 * Reusable read-only email presentation for document previews and the email-view page.
 * A supplied message takes precedence over emailKeyno and uses the supplied attachments.
 * Otherwise, archived content and attachments load separately; attachment failures do not hide
 * the message. HTML is rendered in a sandboxed iframe without scripts or same-origin access.
 * Async rendering uses markForCheck() for OnPush; sender-photo object URLs are owned here.
 */
@Component({
    selector: 'tt-email-viewer',
    templateUrl: './email-viewer.component.html',
    styleUrls: ['./email-viewer.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class EmailViewerComponent implements OnChanges, OnInit, OnDestroy {
    @Input()
    /** Optional preloaded email; when present, no archived-email lookup is needed. */
    public message?: EmailViewerMessage;

    @Input()
    /** Archived email key to load when no preloaded message is supplied. */
    public emailKeyno?: string | number | null;

    @Input()
    /** Attachments associated with a preloaded message. */
    public attachments: EmailViewerAttachment[] = [];

    @Input()
    /** Additional host-provided toolbar actions; the viewer does not implement sending. */
    public actions: EmailViewerAction[] = [];

    @Input()
    /** Placement of reply controls. Defaults to none so a host can expose them in its aside. */
    public replyView: EmailViewerReplyView = 'none';

    @Output()
    public attachmentClick = new EventEmitter<{ attachment: EmailViewerAttachment }>();

    @Output()
    public messageLoaded = new EventEmitter<EmailViewerMessage>();

    @Output()
    public reply = new EventEmitter<MouseEvent>();

    @Output()
    public replyAll = new EventEmitter<MouseEvent>();

    public email?: EmailViewerMessage;

    public displayedAttachments: EmailViewerAttachment[] = [];

    public body?: SafeHtml;

    public loading = false;

    public loadFailed = false;

    public bodyDarkMode = false;

    public recipientsExpanded = false;

    public senderPhotoUrl = '';

    private loadId = 0;

    private photoLoadId = 0;

    private bodyHtml = '';

    private bodyThemeOverridden = false;

    private themeObserver?: MutationObserver;

    constructor(
        private emailService: EmailService,
        private sanitizer: DomSanitizer,
        private state: StateService,
        private graphProfile: GraphProfileService,
        private cdr: ChangeDetectorRef,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {}

    public ngOnInit(): void {
        this.syncBodyThemeToApplication();
        this.themeObserver = new MutationObserver(() => this.syncBodyThemeToApplication());
        this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'style'] });
    }

    public ngOnDestroy(): void {
        this.loadId++;
        this.themeObserver?.disconnect();
        this.photoLoadId++;
        this.releaseSenderPhoto();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['message'] || changes['attachments']) {
            if (this.message) {
                this.showMessage(this.message, this.attachments);
            }
        }

        if (changes['emailKeyno'] && !this.message) {
            void this.loadEmail(this.emailKeyno);
        }
    }

    public get senderInitials(): string {
        const sender = this.email?.sender_name?.trim() || this.email?.sender_email?.trim() || '';
        const parts = sender.split(/\s+/).filter(Boolean);

        return parts
            .slice(0, 2)
            .map((part) => part[0])
            .join('')
            .toUpperCase();
    }

    public get attachmentHeading(): string {
        return this.email?.attatchment_heading?.trim() || 'Attachments';
    }

    public get toRecipients(): string[] {
        return this.splitRecipients(this.email?.recipient_email || this.email?.recipient_name || '');
    }

    public get ccRecipients(): string[] {
        return this.splitRecipients(this.email?.email_cc || '');
    }

    public get canExpandRecipients(): boolean {
        return this.toRecipients.length > 1 || this.ccRecipients.length > 0;
    }

    public get recipientSummary(): string {
        const recipients = this.toRecipients;
        if (!recipients.length) return '';

        const additionalRecipients = recipients.length + this.ccRecipients.length - 1;
        return additionalRecipients > 0 ? `${recipients[0]} +${additionalRecipients}` : recipients[0];
    }

    public getAttachmentName(attachment: EmailViewerAttachment): string {
        return attachment.item_name || attachment.name || '';
    }

    public getAttachmentDescription(attachment: EmailViewerAttachment): string {
        const description = [attachment.contentType, this.formatFileSize(attachment.size)].filter(Boolean);
        return description.join(' - ');
    }

    public getAttachmentIcon(attachment: EmailViewerAttachment): string {
        const icon = attachment.item_glyphicon || 'far-paperclip';
        const style = ['fab', 'fad', 'fal', 'far', 'fas'].find((prefix) => icon.startsWith(`${prefix}-`));

        if (style) return `${style} fa-${icon.slice(style.length + 1)}`;
        if (icon.startsWith('fa-')) return `fa ${icon}`;
        return `glyphicon ${icon}`;
    }

    public trackAttachment(index: number, attachment: EmailViewerAttachment): string | number {
        return attachment.item_id ?? attachment.id ?? attachment.item_path ?? attachment.name ?? index;
    }

    public toggleBodyTheme(): void {
        this.bodyThemeOverridden = true;
        this.bodyDarkMode = !this.bodyDarkMode;
        this.refreshBodyTheme();
    }

    public toggleRecipients(): void {
        if (!this.canExpandRecipients) return;
        this.recipientsExpanded = !this.recipientsExpanded;
    }

    public onSenderPhotoError(): void {
        this.releaseSenderPhoto();
        this.cdr.markForCheck();
    }

    /** Lets an observing host open attachments (for example in a modal), otherwise navigates. */
    public openAttachment(event: MouseEvent, attachment: EmailViewerAttachment): void {
        if (this.attachmentClick.observed) {
            this.attachmentClick.emit({ attachment });
            return;
        }

        if (!attachment.item_state) return;

        const target = { to: attachment.item_state, parms: attachment.item_parms };

        if (event.ctrlKey || event.metaKey) {
            if (this.navigator) {
                this.navigator.newTab(target);
            } else {
                this.state.newTab(attachment.item_state, attachment.item_parms);
            }
        } else if (this.navigator) {
            this.navigator.go(target);
        } else {
            this.state.go(attachment.item_state, attachment.item_parms);
        }
    }

    private async loadEmail(emailKeyno?: string | number | null): Promise<void> {
        const currentLoadId = ++this.loadId;
        this.clear();

        if (emailKeyno === undefined || emailKeyno === null || emailKeyno === '') return;

        this.loading = true;
        const attachmentsPromise = this.emailService.getAttachments({ email_keyno: emailKeyno }).catch(() => []);

        try {
            const message = await this.emailService.getEmail({ email_keyno: emailKeyno });
            const attachments = await attachmentsPromise;

            if (currentLoadId !== this.loadId) return;

            if (!message) {
                this.loadFailed = true;
                this.cdr.markForCheck();
                return;
            }

            this.showMessage(message, attachments);
        } catch {
            if (currentLoadId === this.loadId) {
                this.loadFailed = true;
                this.cdr.markForCheck();
            }
        } finally {
            if (currentLoadId === this.loadId) {
                this.loading = false;
                this.cdr.markForCheck();
            }
        }
    }

    private showMessage(message: EmailViewerMessage, attachments: EmailViewerAttachment[]): void {
        this.loadId++;
        this.email = message;
        this.recipientsExpanded = false;
        this.displayedAttachments = attachments ?? [];
        this.body = this.createSafeBody(message);
        this.loading = false;
        this.loadFailed = false;
        this.messageLoaded.emit(message);
        void this.loadSenderPhoto(message.sender_email);
        this.cdr.markForCheck();
    }

    private clear(): void {
        this.email = undefined;
        this.recipientsExpanded = false;
        this.photoLoadId++;
        this.releaseSenderPhoto();
        this.displayedAttachments = [];
        this.body = undefined;
        this.bodyHtml = '';
        this.loading = false;
        this.loadFailed = false;
    }

    /** Decodes stored base64 HTML or escapes plain text; isolation is provided by the iframe. */
    private createSafeBody(message: EmailViewerMessage): SafeHtml | undefined {
        const encodedBody = message.body || message.email_body_html;
        const html = encodedBody ? this.decodeBase64(encodedBody) : this.escapeText(message.email_body || '');

        if (!html) return undefined;

        this.bodyHtml = html;
        return this.getThemedBody(html);
    }

    /** Adds the selected body palette and the resolved application font size to the email document. */
    private createBodyDocument(html: string): string {
        const root = document.documentElement;
        const styles = getComputedStyle(root);
        const darkMode = this.bodyDarkMode;
        const applicationDarkMode = root.classList.contains('dark');
        const usesApplicationPalette = darkMode === applicationDarkMode;
        const appFontSize = this.getApplicationFontSize();
        const fontFamily = styles.getPropertyValue('--tt-body-font-family').trim() || 'Arial, sans-serif';
        const background = (usesApplicationPalette ? styles.getPropertyValue('--tt-card--color').trim() : '') || (darkMode ? '#191919' : '#fff');
        const textColor = (usesApplicationPalette ? styles.getPropertyValue('--tt-card--color-text').trim() : '') || (darkMode ? '#f4f4f4' : '#333');
        const linkColor = (usesApplicationPalette ? styles.getPropertyValue('--tt-link-color').trim() : '') || (darkMode ? '#6ea8fe' : '#006ee0');
        const headContent = `<meta charset="utf-8"><base target="_blank"><meta name="viewport" content="width=device-width, initial-scale=1"><style>:root{color-scheme:${darkMode ? 'dark' : 'light'};--tt-font-size:${appFontSize};--tt-email-background:${background};--tt-email-text-color:${textColor};--tt-email-link-color:${linkColor};font-size:var(--tt-font-size)!important}html,body{background:var(--tt-email-background)!important;color:var(--tt-email-text-color)!important;font-size:var(--tt-font-size)!important}body{box-sizing:border-box;margin:0;padding:16px;font-family:${fontFamily}!important;overflow-wrap:anywhere}a{color:var(--tt-email-link-color)}img,table{max-width:100%}pre{white-space:pre-wrap}</style>`;
        const head = /<head(?:\s[^>]*)?>/i;
        const htmlElement = /<html(?:\s[^>]*)?>/i;

        if (head.test(html)) return html.replace(head, (match) => `${match}${headContent}`);
        if (htmlElement.test(html)) return html.replace(htmlElement, (match) => `${match}<head>${headContent}</head>`);

        return `<!doctype html><html><head>${headContent}</head><body>${html}</body></html>`;
    }

    private getThemedBody(html: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(this.createBodyDocument(html));
    }

    private refreshBodyTheme(): void {
        if (!this.bodyHtml) {
            this.cdr.markForCheck();
            return;
        }

        this.body = this.getThemedBody(this.bodyHtml);
        this.cdr.markForCheck();
    }

    private syncBodyThemeToApplication(): void {
        if (this.bodyThemeOverridden) return;

        this.bodyDarkMode = document.documentElement.classList.contains('dark');
        this.refreshBodyTheme();
    }

    private async loadSenderPhoto(senderEmail?: string): Promise<void> {
        const currentLoadId = ++this.photoLoadId;
        this.releaseSenderPhoto();

        if (!senderEmail?.trim()) return;

        const photo = await this.graphProfile.getUserPhoto(senderEmail);
        if (currentLoadId !== this.photoLoadId || !photo?.size) return;

        this.senderPhotoUrl = URL.createObjectURL(photo);
        this.cdr.markForCheck();
    }

    private releaseSenderPhoto(): void {
        if (this.senderPhotoUrl) URL.revokeObjectURL(this.senderPhotoUrl);
        this.senderPhotoUrl = '';
    }

    private decodeBase64(value: string): string {
        try {
            const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
            const padding = normalized.length % 4;
            const binary = atob(normalized.padEnd(normalized.length + (padding === 0 ? 0 : 4 - padding), '='));
            return new TextDecoder().decode(Uint8Array.from(binary, (character) => character.charCodeAt(0)));
        } catch {
            return '';
        }
    }

    private escapeText(value: string): string {
        if (!value) return '';

        const element = document.createElement('div');
        element.textContent = value;
        return `<pre>${element.innerHTML}</pre>`;
    }

    private splitRecipients(value: string): string[] {
        return value
            .split(/[;,]/)
            .map((recipient) => recipient.trim())
            .filter(Boolean);
    }

    private getApplicationFontSize(): string {
        const probe = document.createElement('span');
        probe.style.cssText = 'position:fixed;visibility:hidden;pointer-events:none;font-size:var(--tt-font-size)';
        document.body.appendChild(probe);

        const fontSize = getComputedStyle(probe).fontSize;
        probe.remove();

        return fontSize || '16px';
    }

    private formatFileSize(size?: number | null): string {
        if (!size || size < 1) return '';

        if (size < 1024) return `${size} B`;
        if (size < 1024 * 1024) return `${Math.round(size / 102.4) / 10} KB`;
        return `${Math.round(size / (1024 * 102.4)) / 10} MB`;
    }
}

export interface EmailViewerAction {
    id: string;
    title: string;
    icon: string;
    onClick(event: MouseEvent): void;
}

export type EmailViewerReplyView = 'inline' | 'bottom' | 'none';
